import { FilterOperator } from 'types/filters';
import { compareValues } from './operators';

/**
 * Single source of truth for the `archetype` pseudo-property's operator
 * semantics (specs/sphere-archetypes.md "Filter pseudo-property: archetype"),
 * shared by `filterEngine.ts` (graph-service, in-memory evaluation against a
 * fetched-and-parsed `sphere_archetypes` array) and `applicabilityValidator.ts`
 * (the synchronous bag). The Gremlin fragment builder (`gremlinFilterBuilder.ts`)
 * cannot call this — it emits query text, not a boolean — so its fragments are
 * hand-built to agree with the semantics here (see
 * `archetypeFilterGremlinAgreesWithInMemory`).
 *
 * `archetypes` is `undefined` when the sphere carries no `sphere_archetypes`
 * value at all (property absent — e.g. the aspect isn't composed, or no
 * observation has ever been written), distinct from an empty array (the set
 * was explicitly emptied). That distinction is what `exists`/`not_exists` and
 * the `not_in`/`not_contains` asymmetry below depend on, mirroring
 * `operators.ts`'s own absent-value handling exactly rather than approximating
 * it with a default `[]`.
 *
 * `equals` and `contains` are deliberately the SAME element-membership check —
 * `sphere_archetypes` is a set, so "equals" means "is one of the elements",
 * never a substring or whole-array-equality match
 * (`archetypeFilterMatchesWholeNameOnly`).
 */
export function evaluateArchetypeFilter(
  archetypes: string[] | undefined,
  operator: FilterOperator,
  expression: { value?: unknown; values?: unknown[] },
): boolean {
  switch (operator) {
    case 'equals':
    case 'contains':
      return compareValues(archetypes, 'contains', expression.value);
    case 'not_contains':
      return compareValues(archetypes, 'not_contains', expression.value);
    case 'in':
      return (
        Array.isArray(expression.values) &&
        expression.values.some((v) => compareValues(archetypes, 'contains', v))
      );
    case 'contains_all':
      return (
        Array.isArray(expression.values) &&
        expression.values.length > 0 &&
        expression.values.every((v) => compareValues(archetypes, 'contains', v))
      );
    case 'not_in':
      return !(
        Array.isArray(expression.values) &&
        expression.values.some((v) => compareValues(archetypes, 'contains', v))
      );
    case 'exists':
    case 'not_exists':
    case 'is_empty':
    case 'is_not_empty':
      return compareValues(archetypes, operator);
    default:
      // Any operator outside this table fails closed (never-widen) — matches
      // compareValues' own behaviour for an operator outside COMPARATORS.
      return false;
  }
}

/**
 * `archetype.primary` — reads `primary_archetype` (a scalar, not a set), so
 * ordinary `compareValues` applies with no set-membership remapping. Only
 * `equals` and `in` are documented, and the table is CLOSED: every other
 * operator fails closed here, exactly as `buildArchetypePrimaryFragment`
 * emits `MATCH_NOTHING_FRAGMENT` for it.
 *
 * Delegating the rest to `compareValues` would NOT fail closed — `contains`
 * against a scalar string is a substring match, so `contains: 'coach'` would
 * match `center:coach_assistant` in memory while the same expression matched
 * nothing in Gremlin. An Archetype filter matches a whole name or nothing
 * (`archetypeFilterMatchesWholeNameOnly`), and the two engines must agree
 * (`archetypeFilterGremlinAgreesWithInMemory`).
 */
export function evaluateArchetypePrimaryFilter(
  primaryArchetype: string | null | undefined,
  operator: FilterOperator,
  expression: { value?: unknown; values?: unknown[] },
): boolean {
  switch (operator) {
    case 'equals':
      return compareValues(primaryArchetype, 'equals', expression.value);
    case 'in':
      return Array.isArray(expression.values) && compareValues(primaryArchetype, 'in', expression.values);
    default:
      return false;
  }
}
